import Kichiri from "./KichiriService";
import UserService from "./UserService";

/**
 * Backoffice module API. Every call resolves to the response body, or `null` on failure so callers
 * can guard with a single check. Kichiri's error interceptor swallows rejections, so a failed
 * request arrives here as an undefined response rather than a thrown error.
 */
function authToken() {
	let user = UserService.getUserData();
	return user ? user.authToken : null;
}

function errorOf(response) {
	if (!response) return "Request failed";
	let data = response.data || {};
	if (data.error && typeof data.error === "object") return data.error.message || "Request failed";
	return data.error || null;
}

async function call(fn) {
	try {
		let response = await fn(authToken());
		if (!response || !response.data) return { ok: false, error: errorOf(response), data: null };
		let error = errorOf(response);
		if (error) return { ok: false, error, data: null };
		return { ok: true, error: null, data: response.data };
	} catch (error) {
		console.log(error);
		return { ok: false, error: (error && error.message) || "Request failed", data: null };
	}
}

const BackofficeService = {
	// ---------------------------------------------------------------- clients
	listClients() {
		return call(token => Kichiri.backoffice.listClients({}, {}, token));
	},
	listClientContacts({ clientId }) {
		return call(token => Kichiri.backoffice.listClientContacts({}, { clientId }, token));
	},
	createClient({ clientId, contactId, notes }) {
		return call(token => Kichiri.backoffice.createClient({ clientId, contactId, notes }, {}, token));
	},
	updateClient({ backofficeClientId, contactId, notes, status }) {
		return call(token => Kichiri.backoffice.updateClient({ backofficeClientId, contactId, notes, status }, {}, token));
	},
	removeClient({ backofficeClientId }) {
		return call(token => Kichiri.backoffice.removeClient({ backofficeClientId }, {}, token));
	},

	// ---------------------------------------------------------------- tasks
	listTasks(params = {}) {
		return call(token => Kichiri.backoffice.listTasks({}, params, token));
	},
	createTask(payload) {
		return call(token => Kichiri.backoffice.createTask(payload, {}, token));
	},
	updateTask({ taskId, ...payload }) {
		return call(token => Kichiri.backoffice.updateTask(Object.assign({ taskId }, payload), {}, token));
	},
	removeTask({ taskId }) {
		return call(token => Kichiri.backoffice.removeTask({ taskId }, {}, token));
	},
	restoreTask({ taskId }) {
		return call(token => Kichiri.backoffice.restoreTask({ taskId }, {}, token));
	},

	// ---------------------------------------------------------------- accounts
	addTaskAccount({ taskId, name, type, glCode, chartOfAccountId }) {
		return call(token => Kichiri.backoffice.addTaskAccount({ taskId, name, type, glCode, chartOfAccountId }, {}, token));
	},
	updateTaskAccount({ taskId, accountId, ...payload }) {
		return call(token => Kichiri.backoffice.updateTaskAccount(Object.assign({ taskId, accountId }, payload), {}, token));
	},
	removeTaskAccount({ taskId, accountId }) {
		return call(token => Kichiri.backoffice.removeTaskAccount({ taskId, accountId }, {}, token));
	},
	listChartOfAccounts({ clientId }) {
		return call(token => Kichiri.backoffice.listChartOfAccounts({}, { clientId }, token));
	},

	// ---------------------------------------------------------------- email automations
	upsertTaskEmail({ taskId, ...payload }) {
		return call(token => Kichiri.backoffice.upsertTaskEmail(Object.assign({ taskId }, payload), {}, token));
	},
	sendTaskEmailNow({ taskId }) {
		return call(token => Kichiri.backoffice.sendTaskEmailNow({ taskId }, {}, token));
	},
	stopTaskEmail({ taskId, status }) {
		return call(token => Kichiri.backoffice.stopTaskEmail({ taskId, status }, {}, token));
	},
	resumeTaskEmail({ taskId }) {
		return call(token => Kichiri.backoffice.resumeTaskEmail({ taskId }, {}, token));
	},
	removeTaskEmail({ taskId }) {
		return call(token => Kichiri.backoffice.removeTaskEmail({ taskId }, {}, token));
	},
	skipEmailSend({ sendId }) {
		return call(token => Kichiri.backoffice.skipEmailSend({ sendId }, {}, token));
	},
	processDueEmails() {
		return call(token => Kichiri.backoffice.processDueEmails({}, {}, token));
	},

	// ---------------------------------------------------------------- templates
	listTemplates({ includeInactive } = {}) {
		return call(token => Kichiri.backoffice.listTemplates({}, includeInactive ? { includeInactive: "true" } : {}, token));
	},
	createTemplate(payload) {
		return call(token => Kichiri.backoffice.createTemplate(payload, {}, token));
	},
	updateTemplate({ templateId, ...payload }) {
		return call(token => Kichiri.backoffice.updateTemplate(Object.assign({ templateId }, payload), {}, token));
	},
	removeTemplate({ templateId }) {
		return call(token => Kichiri.backoffice.removeTemplate({ templateId }, {}, token));
	},
	previewTemplate({ templateId, clientId, periodStart }) {
		return call(token => Kichiri.backoffice.previewTemplate({ templateId }, { clientId, periodStart }, token));
	},
	applyTemplate({ templateId, ...payload }) {
		return call(token => Kichiri.backoffice.applyTemplate(Object.assign({ templateId }, payload), {}, token));
	},

	// ---------------------------------------------------------------- capacity
	listCapacity() {
		return call(token => Kichiri.backoffice.listCapacity({}, {}, token));
	},
	updateCapacity({ userId, weeklyHours }) {
		return call(token => Kichiri.backoffice.updateCapacity({ userId, weeklyHours }, {}, token));
	},
};

export default BackofficeService;
